import {
  ElementNode,
  type ElementDOMSlot,
  type LexicalNode,
  type NodeKey,
  type SerializedElementNode,
  type Spread,
} from 'lexical';

import { createAsideDom } from './blockDom';

type SerializedNoteLineNode = Spread<
  { lineId: string; status: string; subheading?: boolean },
  SerializedElementNode
>;

const markStatus = (dom: HTMLElement, status: string) => {
  if (status) {
    dom.dataset.status = status;
  } else {
    delete dom.dataset.status;
  }
};

// A line of the note: its blocks in order, as one paragraph of the document.
// What the line shows before and after its text (its controls, its marks)
// is rendered by widgets into the line's lead and tail, which the editor
// does not edit.
export class NoteLineNode extends ElementNode {
  __lineId: string;
  __status: string;
  // A subheading of its section, printed as one.
  __subheading: boolean;

  static getType(): string {
    return 'note-line';
  }

  static clone(node: NoteLineNode): NoteLineNode {
    return new NoteLineNode(
      node.__lineId,
      node.__status,
      node.__subheading,
      node.__key,
    );
  }

  static importJSON(json: SerializedNoteLineNode): NoteLineNode {
    return $createNoteLineNode(
      json.lineId,
      json.status,
      json.subheading,
    ).updateFromJSON(json);
  }

  constructor(lineId = '', status = '', subheading = false, key?: NodeKey) {
    super(key);
    this.__lineId = lineId;
    this.__status = status;
    this.__subheading = subheading;
  }

  exportJSON(): SerializedNoteLineNode {
    return {
      ...super.exportJSON(),
      lineId: this.getLineId(),
      status: this.getStatus(),
      subheading: this.isSubheading(),
    };
  }

  createDOM(): HTMLElement {
    const dom = document.createElement('div');
    dom.dataset.noteLineId = this.__lineId;
    markStatus(dom, this.__status);
    dom.toggleAttribute('data-subheading', this.__subheading);
    dom.append(
      createAsideDom('span', 'line-lead'),
      createAsideDom('span', 'line-tail'),
    );
    return dom;
  }

  updateDOM(previous: NoteLineNode, dom: HTMLElement): boolean {
    if (previous.__lineId !== this.__lineId) {
      dom.dataset.noteLineId = this.__lineId;
    }
    if (previous.__status !== this.__status) {
      markStatus(dom, this.__status);
    }
    if (previous.__subheading !== this.__subheading) {
      dom.toggleAttribute('data-subheading', this.__subheading);
    }
    return false;
  }

  getDOMSlot(element: HTMLElement): ElementDOMSlot<HTMLElement> {
    const slot = super
      .getDOMSlot(element)
      .withAfter(element.firstElementChild)
      .withBefore(element.lastElementChild);
    // Lexical writes a caret between the line's children after the lead,
    // but reads one back as the offset among all of the line's DOM, lead
    // included; read back here, it is the child index it wrote.
    const resolveChildIndex: ElementDOMSlot<HTMLElement>['resolveChildIndex'] =
      (node, elementDOM, initialDOM, initialOffset) =>
        initialDOM === slot.element
          ? [
              node,
              Math.min(
                node.getChildrenSize(),
                Math.max(0, initialOffset - slot.getFirstChildOffset()),
              ),
            ]
          : slot.resolveChildIndex(node, elementDOM, initialDOM, initialOffset);
    return Object.assign(Object.create(slot), { resolveChildIndex });
  }

  getLineId(): string {
    return this.getLatest().__lineId;
  }

  // A line Enter added, or the empty line of a section that shows none,
  // until the note holds it under an id of its own.
  isDraft(): boolean {
    return this.getLineId().startsWith(NOTE_DRAFT_LINE_PREFIX);
  }

  // The empty line of a section that shows none.
  isSectionDraft(): boolean {
    return this.getLineId().startsWith(NOTE_SECTION_DRAFT_PREFIX);
  }

  setLineId(lineId: string): this {
    const writable = this.getWritable();
    writable.__lineId = lineId;
    return writable;
  }

  getStatus(): string {
    return this.getLatest().__status;
  }

  setStatus(status: string): this {
    const writable = this.getWritable();
    writable.__status = status;
    return writable;
  }

  isSubheading(): boolean {
    return this.getLatest().__subheading;
  }

  setSubheading(subheading: boolean): this {
    const writable = this.getWritable();
    writable.__subheading = subheading;
    return writable;
  }
}

export const NOTE_DRAFT_LINE_PREFIX = 'draft:';

const NOTE_SECTION_DRAFT_PREFIX = `${NOTE_DRAFT_LINE_PREFIX}section:`;

export const noteSectionDraftLineId = (sectionId: string) =>
  `${NOTE_SECTION_DRAFT_PREFIX}${sectionId}`;

export const $createNoteLineNode = (
  lineId: string,
  status = '',
  subheading = false,
) => new NoteLineNode(lineId, status, subheading);

export const $createNoteDraftLine = () =>
  $createNoteLineNode(`${NOTE_DRAFT_LINE_PREFIX}${crypto.randomUUID()}`);

export const $isNoteLineNode = (
  node: LexicalNode | null | undefined,
): node is NoteLineNode => node instanceof NoteLineNode;
